import { JSON_PRODUCT_MAJIK_BUWIZ } from "./_products/product-majik-buwiz";
import { JSON_PRODUCT_MAJIK_MESSAGE } from "./_products/product-majik-message";
import { JSON_PRODUCT_MAJIK_SIGNATURE } from "./_products/product-majik-signature";
import { JSON_PRODUCT_MAJIK_SLINK } from "./_products/product-majik-slink";
import { JSON_PRODUCT_MAJIK_UNIVERSAL_ID } from "./_products/product-majik-uid";
import { ProductPageData } from "./_types";

export const allMajikahProduct: ProductPageData[] = [
  JSON_PRODUCT_MAJIK_SIGNATURE,
  JSON_PRODUCT_MAJIK_UNIVERSAL_ID,
  JSON_PRODUCT_MAJIK_SLINK,
  JSON_PRODUCT_MAJIK_BUWIZ,
  JSON_PRODUCT_MAJIK_MESSAGE,
];

// Function to get content by ID
export function getProductByID(id: string) {
  return allMajikahProduct.find((content) => content.id === id);
}

// Function to get content by Category
export function getProductsByCategory(category: string): ProductPageData[] {
  if (!category || !Array.isArray(allMajikahProduct)) {
    return [];
  }

  const fetchedProducts = allMajikahProduct.filter(
    (content) => content.metadata.category === category,
  );

  if (fetchedProducts.length > 1) {
    const sortedProducts = fetchedProducts.sort(
      (a: ProductPageData, b: ProductPageData) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );

    return sortedProducts;
  } else {
    return fetchedProducts;
  }
}

export function getProductsBySubCategory(category: string): ProductPageData[] {
  if (!category || !Array.isArray(allMajikahProduct)) {
    return [];
  }

  return allMajikahProduct.filter(
    (content) => content.metadata.subcategory === category,
  );
}

export function getProductsByAllCategories(
  category: string,
): ProductPageData[] {
  if (!category || !Array.isArray(allMajikahProduct)) {
    return [];
  }

  let byCategory = getProductsByCategory(category);
  let bySubCategory = getProductsBySubCategory(category);

  if (byCategory.length > 1) {
    byCategory = byCategory.sort(
      (a: ProductPageData, b: ProductPageData) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }

  if (bySubCategory.length > 1) {
    bySubCategory = bySubCategory.sort(
      (a: ProductPageData, b: ProductPageData) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }
  // Optionally deduplicate items by ID if they exist in both
  const categoryIds = new Set(byCategory.map((tool) => tool.id));
  const uniqueSubCategory = bySubCategory.filter(
    (tool) => !categoryIds.has(tool.id),
  );

  const fetchedProducts = [...byCategory, ...uniqueSubCategory];

  return fetchedProducts;
}

export function getRelatedProductsByAllCategories(
  category: string,
  currentProductID: string | null = null,
): ProductPageData[] {
  if (!category || !Array.isArray(allMajikahProduct)) {
    return [];
  }

  const byCategory = getProductsByCategory(category);
  const bySubCategory = getProductsBySubCategory(category);

  // Optionally deduplicate items by ID if they exist in both
  const categoryIds = new Set(byCategory.map((tool) => tool.id));
  const uniqueSubCategory = bySubCategory.filter(
    (tool) => !categoryIds.has(tool.id),
  );

  let combined = [...byCategory, ...uniqueSubCategory];

  // If currentProductID is set, remove that tool from the results
  if (currentProductID) {
    combined = combined.filter((tool) => tool.id !== currentProductID);
  }

  return combined;
}

// Function to get content by Name
export function getProductByName(name: string) {
  return allMajikahProduct.find(
    (content) => content.metadata.productName === name,
  );
}

type ProductSort = "timestamp" | "name";
type ProductOrder = "normal" | "reverse";

export function getAllProducts(
  sort: ProductSort = "name",
  order: ProductOrder = "normal",
): ProductPageData[] {
  if (!Array.isArray(allMajikahProduct)) {
    return [];
  }

  const products = [...allMajikahProduct];

  products.sort((a, b) => {
    if (sort === "timestamp") {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    }

    // Default: sort by product name alphabetically
    return a.metadata.productName.localeCompare(
      b.metadata.productName,
      undefined,
      { sensitivity: "base" },
    );
  });

  if (order === "reverse") {
    products.reverse();
  }

  return products;
}
